import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Formik, FormikHelpers, FormikProps, FormikState } from 'formik';
import { Col, PageHeader } from 'react-bootstrap';
import { InjectedProps } from '../../types';
import { Regions } from '../../types/Regions';
import ControlButtonGroup from '../misc/ControlButtonGroup';
import RegionsForm, { IRegions } from './RegionsForm';
import { emptyRegions } from '../../store/initial';
import DefaultRoutes from '../../routes/DefaultRoutes';

interface RegionsEditState {
    editing: boolean;
}

@inject('store')
@observer
export class RegionsEdit extends React.Component<{}, RegionsEditState> {
    public get injected() {
        return this.props as InjectedProps;
    }

    public state = {
        editing: false
    };

    public initialValues = (regions: Regions) => {
        const { isLoading } = this.injected.store;
        return isLoading ? emptyRegions : regions;
    };

    public get id() {
        return this.injected.store.router.params.id ? parseInt(this.injected.store.router.params.id, 10) : 0;
    }

    public getRegions() {
        return this.injected.store.regions.getItem(this.id);
    }

    public fetchData() {
        const { fetchRegions } = this.injected.store;
        return fetchRegions(this.id);
    }

    public componentDidMount() {
        return this.fetchData();
    }

    public render() {
        const regions = this.getRegions();
        const initialValues = this.initialValues(regions);

        if (!initialValues) {
            return null;
        }

        return (
            <>
                <PageHeader>Regions</PageHeader>
                <Col className="fixed-controls">
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize
                        onSubmit={this.handleSubmit}
                    >{this.renderForm()}</Formik>
                </Col>
            </>
        );
    }

    public renderForm = () => {
        return RegionsForm({
            editing: this.state.editing,
            controls: (bag: FormikProps<IRegions>) => (
                <ControlButtonGroup
                    editing={this.state.editing}
                    canEdit
                    canDelete={this.state.editing}
                    handleEdit={this.toggleEdit}
                    handleCancel={this.handleCancel(bag)}
                    handleBack={this.handleBack}
                    handleDelete={this.handleDelete}
                />
            )
        });
    };

    public toggleEdit = () => {
        this.setState({ editing: !this.state.editing });
    };

    public handleCancel = (bag: FormikProps<IRegions>) => () => {
        this.setState({ editing: false });
        bag.resetForm(bag.initialValues as Partial<FormikState<IRegions>>);
    };

    public handleBack = () => {
        const { router } = this.injected.store;
        router.goTo(DefaultRoutes.RegionsList, {}, this.injected.store);
    };

    public handleSubmit = (values: Regions, actions: FormikHelpers<Regions>) => {
        actions.setSubmitting(true);
        const { regions } = this.injected.store;
        return regions
            .update(values)
            .then(() => {
                actions.setSubmitting(false);
                this.setState({ editing: false });
            })
            .catch(error => {
                actions.setErrors(error.response.data);
                actions.setSubmitting(false);
            });
    };

    protected handleDelete = () => {
        if (window.confirm('Are you sure you wish to delete this item?')) {
            this.injected.store.deleteRegions(this.id);
            this.handleBack();
        }
        return;
    };
}

export default RegionsEdit;
